import { useEffect, useState } from 'react';
import { apiClient, type WorkspaceDetail, type MeResponse, ApiError } from '@/lib/api/client';
import { IdentityEditor } from '@/components/IdentityEditor';
import { useDialogFocus } from '@/lib/useDialogFocus';
import { QuietHoursForm } from '@/components/QuietHoursForm';
import { cn } from '@/lib/utils';

/**
 * Workspace settings — per the share-model pivot (2026-05-22) workspaces are
 * single-owner personal sort-folders. Collaboration lives on PROJECTS, so this
 * drawer only exposes "General" (rename + slug). No Members tab. No Invites tab.
 */

interface Props {
  onClose: () => void;
  onChanged: () => void;
  /** Used to seed the QuietHoursForm with current user-level TZ + quiet hours. */
  me: MeResponse;
}

type UserPrefs = {
  timezone: string | null;
  quiet_start_local: string | null;
  quiet_end_local: string | null;
};

export function SettingsDrawer({ onClose, onChanged, me }: Props) {
  const [ws, setWs] = useState<WorkspaceDetail | null>(null);
  const [prefs, setPrefs] = useState<UserPrefs | null>(null);
  const [dirty, setDirty] = useState(false);
  // Two-step discard confirm — was native window.confirm() (banned per
  // DESIGN.md anti-patterns). First close attempt while dirty arms
  // `pendingDiscard` + 3s revert; second close within the window commits.
  const [pendingDiscard, setPendingDiscard] = useState(false);
  const panelRef = useDialogFocus<HTMLDivElement>();

  function tryClose() {
    if (!dirty) {
      onClose();
      return;
    }
    if (!pendingDiscard) {
      setPendingDiscard(true);
      setTimeout(() => setPendingDiscard(false), 3000);
      return;
    }
    onClose();
  }

  async function refresh() {
    const w = await apiClient.currentWorkspace();
    setWs(w);
    // Read user-level TZ + quiet hours from the telegram status endpoint —
    // that's where they live regardless of whether Telegram is linked. (The
    // endpoint name is legacy; the values are user-scoped.)
    try {
      const s = await apiClient.telegramStatus();
      setPrefs({
        timezone: s.timezone,
        quiet_start_local: s.quiet_start_local,
        quiet_end_local: s.quiet_end_local,
      });
    } catch {
      // If status fails, leave prefs null — section will skeleton.
    }
  }

  useEffect(() => {
    refresh().catch(console.error);
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') tryClose();
    }
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="ws-settings-title"
      className="fixed inset-0 z-50 flex justify-end"
    >
      <div className="flex-1 bg-black/60" onClick={tryClose} aria-hidden />
      <div ref={panelRef} className="flex w-full sm:max-w-2xl flex-col bg-background shadow-2xl">
        <header className="flex items-center justify-between border-b border-border px-6 py-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Workspace settings
            </div>
            <h2 id="ws-settings-title" className="text-lg font-semibold">{ws?.name ?? 'Loading…'}</h2>
          </div>
          <div className="flex items-center gap-2">
            {pendingDiscard && (
              <span
                role="status"
                className="font-mono text-[10px] uppercase tracking-widest text-destructive"
              >
                Discard changes? click again
              </span>
            )}
            <button
              type="button"
              onClick={tryClose}
              className={cn(
                'grid size-7 place-items-center rounded-md border transition-colors',
                pendingDiscard
                  ? 'border-destructive/70 bg-destructive/15 text-destructive'
                  : 'border-border text-muted-foreground hover:border-ring/60 hover:text-foreground',
              )}
              aria-label={pendingDiscard ? 'Confirm discard (3s)' : 'Close settings (Esc)'}
              title={pendingDiscard ? 'Click again to discard unsaved changes' : 'Close (Esc)'}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden>
                <path d="M3 3 L11 11 M11 3 L3 11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </header>
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-8">
          {ws && (
            <>
              <GeneralTab
                ws={ws}
                onDirtyChange={setDirty}
                onSaved={() => {
                  setDirty(false);
                  refresh();
                  onChanged();
                }}
              />
              <NotificationsSection prefs={prefs} onSaved={refresh} userEmail={me.user.email} />
              <DangerZone wsName={ws.name} />
            </>
          )}
        </div>
        <footer className="border-t border-border px-6 py-4">
          <p className="text-xs text-muted-foreground">
            Share work in projects, not workspaces. Each project has its own members and roles.
            Workspaces stay private to you as a personal sort-folder.
          </p>
        </footer>
      </div>
    </div>
  );
}

function GeneralTab({
  ws,
  onSaved,
  onDirtyChange,
}: {
  ws: WorkspaceDetail;
  onSaved: () => void;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const [name, setName] = useState(ws.name);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const dirty = !!name.trim() && name.trim() !== ws.name;

  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  async function save() {
    setSaving(true);
    setErr(null);
    try {
      await apiClient.updateWorkspace({ name: name.trim() });
      onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <Field label="Identity">
        <IdentityEditor
          name={ws.name}
          image_key={ws.image_key}
          emoji={ws.emoji}
          color={null}
          onUploadImage={async (file) => {
            await apiClient.uploadWorkspaceImage(file);
            onSaved();
          }}
          onRemoveImage={async () => {
            await apiClient.deleteWorkspaceImage();
            onSaved();
          }}
          onSaveEmoji={async (emoji) => {
            await apiClient.updateWorkspace({ emoji });
            onSaved();
          }}
        />
      </Field>
      <Field label="Workspace name">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={saving}
          maxLength={64}
          className="block w-full rounded-md border border-border bg-card px-3 py-2 text-sm focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30 disabled:opacity-60"
        />
      </Field>
      <Field label="Slug">
        <div className="font-mono text-sm text-muted-foreground">/w/{ws.slug}</div>
      </Field>
      {err && (
        <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm">
          {err}
        </div>
      )}
      <button
        onClick={save}
        disabled={!dirty || saving}
        className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {saving ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <label className="block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        {label}
      </label>
      {children}
    </div>
  );
}

/** Notifications — user-level TZ + quiet hours. Accessible from the workspace
 *  settings drawer so users who haven't linked Telegram can still configure
 *  these (previously gated behind the Telegram link-flow). */
function NotificationsSection({
  prefs,
  onSaved,
  userEmail,
}: {
  prefs: UserPrefs | null;
  onSaved: () => void;
  userEmail: string;
}) {
  return (
    <div className="space-y-2">
      <div className="space-y-1">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Notifications
        </div>
        <p className="text-xs text-muted-foreground">
          User-level settings for{' '}
          <span className="font-mono text-foreground">{userEmail}</span>. Apply across all your
          workspaces.
        </p>
      </div>
      {prefs ? (
        <QuietHoursForm
          timezone={prefs.timezone}
          quietStart={prefs.quiet_start_local}
          quietEnd={prefs.quiet_end_local}
          onSaved={onSaved}
        />
      ) : (
        <div className="h-32 animate-pulse rounded-md bg-background/40" aria-busy="true" />
      )}
    </div>
  );
}

/** Danger zone — workspace delete is creator-only with type-name-to-confirm
 *  per feedback_apetech_pm_destructive_actions. Backend isn't wired yet so
 *  the button is disabled with explanatory copy; the surface is here so users
 *  see the path exists and we don't need to ship a UI refactor later. */
function DangerZone({ wsName }: { wsName: string }) {
  return (
    <div className="space-y-2 rounded-md border border-destructive/30 bg-destructive/5 p-4">
      <div className="font-mono text-[10px] uppercase tracking-widest text-destructive">
        Danger zone
      </div>
      <div className="space-y-1">
        <div className="text-sm font-semibold text-foreground">Delete workspace</div>
        <p className="text-xs text-muted-foreground">
          Removes <span className="text-foreground">{wsName}</span> and every project filed
          directly under it. 30-day soft-delete with restorable Trash. Projects shared with you
          (pinned here from others' workspaces) are unaffected.
        </p>
      </div>
      <button
        type="button"
        disabled
        title="Workspace delete ships after the project-trash audit lands"
        className="rounded-md border border-destructive/40 px-3 py-1.5 text-xs font-medium text-destructive/70 disabled:cursor-not-allowed disabled:opacity-60"
      >
        Delete workspace (coming soon)
      </button>
    </div>
  );
}
